package com.bikompanion.ui.composables

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.bikompanion.domain.BikeStation
import com.bikompanion.ui.preview.PreviewEmptyStation
import com.bikompanion.ui.preview.PreviewStation
import com.bikompanion.ui.preview.PreviewSurface

internal val NormalBikeColor = Color(0xFF3B82F6)
internal val ElectricBikeColor = Color(0xFFF59E0B)
internal val EmptySlotColor = Color(0xFFD1D5DB)

private val RingStrokeWidth = 4.dp
private val DotOutlineWidth = 1.dp

/**
 * The single colour a dot takes when it only has one kind of bike to report.
 *
 * Grey is kept for a station with nothing to take, which is the one thing worth seeing from
 * across a city.
 */
internal fun stationDotColor(station: BikeStation): Color =
    when {
        station.electricBikes > 0 -> ElectricBikeColor
        station.freeBikes > 0 -> NormalBikeColor
        else -> EmptySlotColor
    }

/**
 * Draws a station too small to carry its numbers.
 *
 * A station holding both kinds is split down the middle rather than given one colour: at this
 * size that split is the only way to tell "some e-bikes here" from "e-bikes only", and it
 * costs no more room than the plain dot does.
 */
internal fun DrawScope.drawStationDot(
    center: Offset,
    radiusPx: Float,
    station: BikeStation,
) {
    val normal = station.normalBikes.coerceAtLeast(0)
    val electric = station.electricBikes.coerceAtLeast(0)
    if (normal > 0 && electric > 0) {
        val topLeft = Offset(center.x - radiusPx, center.y - radiusPx)
        val halves = Size(radiusPx * 2f, radiusPx * 2f)
        // -90° starts at the top, so the first sweep takes the right half and the second the left.
        drawArc(
            color = NormalBikeColor,
            startAngle = -90f,
            sweepAngle = 180f,
            useCenter = true,
            topLeft = topLeft,
            size = halves,
        )
        drawArc(
            color = ElectricBikeColor,
            startAngle = 90f,
            sweepAngle = 180f,
            useCenter = true,
            topLeft = topLeft,
            size = halves,
        )
    } else {
        drawCircle(color = stationDotColor(station), radius = radiusPx, center = center)
    }
    // Keeps dots apart from each other and off the map underneath them.
    drawCircle(
        color = Color.White,
        radius = radiusPx,
        center = center,
        style = Stroke(DotOutlineWidth.toPx()),
    )
}

/**
 * Draws a station's (or a group's) mix of bikes as a pie centred on [center].
 *
 * [detail] morphs between the two readings of the same shape: at `0` the stroke is as wide as
 * the radius, so the pie reads as a solid dot at map-wide zoom; at `1` it thins into a ring.
 * Driving both ends off one stroke width means zooming opens the hole gradually instead of
 * swapping one drawing for another.
 */
internal fun DrawScope.drawStationPie(
    center: Offset,
    diameterPx: Float,
    normalBikes: Int,
    electricBikes: Int,
    emptySlots: Int,
    detail: Float,
) {
    val normal = normalBikes.coerceAtLeast(0)
    val electric = electricBikes.coerceAtLeast(0)
    val empty = emptySlots.coerceAtLeast(0)
    val total = normal + electric + empty

    val radius = diameterPx / 2f
    val strokeWidth = lerp(radius, RingStrokeWidth.toPx().coerceAtMost(radius), detail.coerceIn(0f, 1f))
    val ringStyle = Stroke(strokeWidth)
    val topLeft = Offset(center.x - radius + strokeWidth / 2f, center.y - radius + strokeWidth / 2f)
    val arcSize = Size(diameterPx - strokeWidth, diameterPx - strokeWidth)

    if (total <= 0) {
        drawArc(
            color = EmptySlotColor,
            startAngle = 0f,
            sweepAngle = 360f,
            useCenter = false,
            topLeft = topLeft,
            size = arcSize,
            style = ringStyle,
        )
        return
    }

    var startAngle = -90f
    drawSegment(normal, total, NormalBikeColor, startAngle, topLeft, arcSize, ringStyle)?.let { startAngle = it }
    drawSegment(electric, total, ElectricBikeColor, startAngle, topLeft, arcSize, ringStyle)?.let { startAngle = it }
    drawSegment(empty, total, EmptySlotColor, startAngle, topLeft, arcSize, ringStyle)
}

/** Draws one slice and returns the angle the next one starts at, or null if there was nothing to draw. */
private fun DrawScope.drawSegment(
    count: Int,
    total: Int,
    color: Color,
    startAngle: Float,
    topLeft: Offset,
    arcSize: Size,
    style: Stroke,
): Float? {
    if (count <= 0) return null
    val sweep = 360f * count / total
    drawArc(
        color = color,
        startAngle = startAngle,
        sweepAngle = sweep,
        useCenter = false,
        topLeft = topLeft,
        size = arcSize,
        style = style,
    )
    return startAngle + sweep
}

/** Standalone version of [drawStationPie], for previews and one-off use outside the map overlay. */
@Composable
internal fun StationPieCircle(
    station: BikeStation,
    diameter: Dp,
    detail: Float = 1f,
) {
    Canvas(modifier = Modifier.size(diameter)) {
        drawStationPie(
            center = center,
            diameterPx = size.minDimension,
            normalBikes = station.normalBikes,
            electricBikes = station.electricBikes,
            emptySlots = station.emptySlots,
            detail = detail,
        )
    }
}

internal fun lerp(
    start: Float,
    stop: Float,
    fraction: Float,
) = start + (stop - start) * fraction

internal fun lerpDp(
    start: Dp,
    stop: Dp,
    fraction: Float,
) = start + (stop - start) * fraction.coerceIn(0f, 1f)

// Every mix a station can hold: both kinds, one kind, nothing to take, and no reading at all.
private val PreviewStationMixes = listOf(
    PreviewStation,
    PreviewStation.copy(electricBikes = 0),
    PreviewStation.copy(electricBikes = PreviewStation.freeBikes),
    PreviewEmptyStation,
    PreviewEmptyStation.copy(emptySlots = 0),
)

@Preview
@Composable
private fun StationPieCirclePreview() {
    PreviewSurface {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.padding(16.dp),
        ) {
            PreviewStationMixes.forEach { station ->
                StationPieCircle(station = station, diameter = 56.dp)
            }
        }
    }
}

/** The same shape as it morphs from the solid dot of a wide zoom into the ring of a close one. */
@Preview
@Composable
private fun StationPieCircleDetailPreview() {
    PreviewSurface {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.padding(16.dp),
        ) {
            listOf(0f, 0.25f, 0.5f, 0.75f, 1f).forEach { detail ->
                StationPieCircle(station = PreviewStation, diameter = 56.dp, detail = detail)
            }
        }
    }
}

@Preview
@Composable
private fun StationDotPreview() {
    PreviewSurface {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.padding(16.dp),
        ) {
            PreviewStationMixes.forEach { station ->
                Canvas(modifier = Modifier.size(24.dp)) {
                    drawStationDot(center = center, radiusPx = size.minDimension / 2f, station = station)
                }
            }
        }
    }
}
